import { ArrayAlignment, GraftResolutionMode, MergePriority, PatchDirection, ThreeWayMergeMode } from "../api/commonConfig";
import { Utilities } from "../Utilities";
import { NavigatorData, Operations } from "./NavigatorData"

export interface ActionPayload {
    formData: BaseConfiguration;
    operationType: Operations;
}


export interface BaseConfiguration {
    ArrayAlignmentOption: ArrayAlignment,
    WordByWordOption: boolean,
    ThreeWayMergeModeOption: ThreeWayMergeMode,
    MergePriorityOption: MergePriority,
    PatchDirectionOption: PatchDirection,
    GraftResolutionModeOption: GraftResolutionMode;
    InputFilenameA: string,
    InputFilenameB: string,
    InputFileA: File | null,
    InputFileB: File | null,
    ResultFilename: string,
    JobId: string,
    isDirty: boolean,
    isValid: boolean,
    AncestorFilename?: string,
    AncestorFile?: File | null,
    ErrorData: ErrorData,
    ResultFileState: ResultFileState,
    JobDescriptor: string
}

export interface ErrorData {
    inputFileA: string | null,
    inputFileB: string | null,
    resultFile: string | null,
    ancestorFile: string | null
}

export enum ResultFileState {
    initial,
    inProgress,
    completedOk,
    failedToComplete,
    completedOkButStale // config has changed since operation was run
}

export const ArrayAlignmentName = Utilities.nameof<BaseConfiguration>('ArrayAlignmentOption');
export const WordByWordName = Utilities.nameof<BaseConfiguration>('WordByWordOption');
export const ThreeWayMergeModeName = Utilities.nameof<BaseConfiguration>('ThreeWayMergeModeOption');
export const MergePriorityName = Utilities.nameof<BaseConfiguration>('MergePriorityOption');
export const PatchDirectionName = Utilities.nameof<BaseConfiguration>('PatchDirectionOption');
export const GraftResolutionModeName = Utilities.nameof<BaseConfiguration>('GraftResolutionModeOption');
export const InputFilenameAId = Utilities.nameof<BaseConfiguration>('InputFilenameA');
export const InputFilenameBId = Utilities.nameof<BaseConfiguration>('InputFilenameB');
export const InputFileA = Utilities.nameof<BaseConfiguration>('InputFileA');
export const InputFileB = Utilities.nameof<BaseConfiguration>('InputFileB');
export const ResultFilename = Utilities.nameof<BaseConfiguration>('ResultFilename');
export const AncestorFilename = Utilities.nameof<BaseConfiguration>('AncestorFilename');
export const AncestorFile = Utilities.nameof<BaseConfiguration>('AncestorFile');
export const JobDescriptor = Utilities.nameof<BaseConfiguration>('JobDescriptor');

export default interface FormDataType {
    [Operations.compare]: BaseConfiguration,
    [Operations.threeWayMerge]: BaseConfiguration,
    [Operations.twoWayMerge]: BaseConfiguration,
    [Operations.graft]: BaseConfiguration,
    [Operations.patch]: BaseConfiguration
}

export const ControlVisbility = {
    [AncestorFilename]: [Operations.threeWayMerge],
    [ThreeWayMergeModeName]: [Operations.threeWayMerge],
}


export default class ConfigFormData {
    static controlVisbility = {
        [AncestorFilename]: [Operations.threeWayMerge],
        [ThreeWayMergeModeName]: [Operations.threeWayMerge],
    }
    public static getControlVisibility = (controlName: string, operationType: Operations) => {
        let visibleControlList = ConfigFormData.controlVisbility[controlName]
        if (visibleControlList) {
            return visibleControlList.includes(operationType);
        } else {
            return true;
        }
    }

    public static getPartOfName = (nameType: string, operationType: Operations) => {
        let suffix: string = '';
        switch (nameType) {
            case AncestorFilename:
            case InputFilenameAId:
            case InputFilenameBId:
                suffix = NavigatorData.getSuffixForOperation(operationType);
                break;
            case JobDescriptor:
                suffix = operationType + '...';
                break;
            case ResultFilename:
                suffix = `result-${NavigatorData.getSuffixForOperation(operationType)}`
                break;
        }
        return suffix;
    }

    public static defaults: FormDataType = {
        [Operations.compare]: {
            ArrayAlignmentOption: ArrayAlignment.orderless,
            WordByWordOption: false,
            MergePriorityOption: MergePriority.a,
            ThreeWayMergeModeOption: ThreeWayMergeMode.showConflicts,
            PatchDirectionOption: PatchDirection.aTob,
            GraftResolutionModeOption: GraftResolutionMode.changesetPriority,
            InputFilenameA: `${ConfigFormData.getPartOfName(InputFilenameAId, Operations.compare)}1A.json`,
            InputFilenameB: `${ConfigFormData.getPartOfName(InputFilenameBId, Operations.compare)}1B.json`,
            InputFileA: null,
            InputFileB: null,
            ResultFilename: `${ConfigFormData.getPartOfName(ResultFilename, Operations.compare)}-1.json`,
            JobId: '',
            isDirty: false,
            isValid: false,
            ErrorData: { inputFileA: Utilities.emptyFile, inputFileB: Utilities.emptyFile, resultFile: null, ancestorFile: null } as ErrorData,
            ResultFileState: ResultFileState.initial,
            JobDescriptor: `${ConfigFormData.getPartOfName(JobDescriptor, Operations.compare)}`
        },
        [Operations.threeWayMerge]: {
            ArrayAlignmentOption: ArrayAlignment.orderless,
            WordByWordOption: false,
            MergePriorityOption: MergePriority.a,
            ThreeWayMergeModeOption: ThreeWayMergeMode.showConflicts,
            PatchDirectionOption: PatchDirection.aTob,
            GraftResolutionModeOption: GraftResolutionMode.changesetPriority,
            InputFilenameA: `${ConfigFormData.getPartOfName(InputFilenameAId, Operations.threeWayMerge)}1A.json`,
            InputFilenameB: `${ConfigFormData.getPartOfName(InputFilenameBId, Operations.threeWayMerge)}1B.json`,
            InputFileA: null,
            InputFileB: null,
            AncestorFile: null,
            ResultFilename: `${ConfigFormData.getPartOfName(ResultFilename, Operations.threeWayMerge)}-1.json`,
            JobId: '',
            isDirty: false,
            isValid: false,
            AncestorFilename: `${ConfigFormData.getPartOfName(AncestorFilename, Operations.threeWayMerge)}Ancestor.json`,
            ErrorData: { inputFileA: Utilities.emptyFile, inputFileB: Utilities.emptyFile, resultFile: null, ancestorFile: Utilities.emptyFile } as ErrorData,
            ResultFileState: ResultFileState.initial,
            JobDescriptor: `${ConfigFormData.getPartOfName(JobDescriptor, Operations.threeWayMerge)}`
        },
        [Operations.twoWayMerge]: {
            ArrayAlignmentOption: ArrayAlignment.orderless,
            WordByWordOption: false,
            MergePriorityOption: MergePriority.a,
            ThreeWayMergeModeOption: ThreeWayMergeMode.showConflicts,
            PatchDirectionOption: PatchDirection.aTob,
            GraftResolutionModeOption: GraftResolutionMode.changesetPriority,
            InputFilenameA: `${ConfigFormData.getPartOfName(InputFilenameAId, Operations.twoWayMerge)}1A.json`,
            InputFilenameB: `${ConfigFormData.getPartOfName(InputFilenameBId, Operations.twoWayMerge)}1B.json`,
            InputFileA: null,
            InputFileB: null,
            ResultFilename: `${ConfigFormData.getPartOfName(ResultFilename, Operations.twoWayMerge)}-1.json`,
            JobId: '',
            isDirty: false,
            isValid: false,
            ErrorData: { inputFileA: Utilities.emptyFile, inputFileB: Utilities.emptyFile, resultFile: null, ancestorFile: null } as ErrorData,
            ResultFileState: ResultFileState.initial,
            JobDescriptor: `${ConfigFormData.getPartOfName(JobDescriptor, Operations.twoWayMerge)}`
        },
        [Operations.graft]: {
            ArrayAlignmentOption: ArrayAlignment.orderless,
            WordByWordOption: false,
            MergePriorityOption: MergePriority.a,
            ThreeWayMergeModeOption: ThreeWayMergeMode.showConflicts,
            PatchDirectionOption: PatchDirection.aTob,
            GraftResolutionModeOption: GraftResolutionMode.changesetPriority,
            InputFilenameA: `${ConfigFormData.getPartOfName(InputFilenameAId, Operations.graft)}1A.json`,
            InputFilenameB: `${ConfigFormData.getPartOfName(InputFilenameBId, Operations.graft)}1B.json`,
            InputFileA: null,
            InputFileB: null,
            ResultFilename: `${ConfigFormData.getPartOfName(ResultFilename, Operations.graft)}-1.json`,
            JobId: '',
            isDirty: false,
            isValid: false,
            ErrorData: { inputFileA: Utilities.emptyFile, inputFileB: Utilities.emptyFile, resultFile: null, ancestorFile: null } as ErrorData,
            ResultFileState: ResultFileState.initial,
            JobDescriptor: `${ConfigFormData.getPartOfName(JobDescriptor, Operations.graft)}`
        },
        [Operations.patch]: {
            ArrayAlignmentOption: ArrayAlignment.orderless,
            WordByWordOption: false,
            MergePriorityOption: MergePriority.a,
            ThreeWayMergeModeOption: ThreeWayMergeMode.showConflicts,
            PatchDirectionOption: PatchDirection.aTob,
            GraftResolutionModeOption: GraftResolutionMode.changesetPriority,
            InputFilenameA: `${ConfigFormData.getPartOfName(InputFilenameAId, Operations.patch)}1A.json`,
            InputFilenameB: `${ConfigFormData.getPartOfName(InputFilenameBId, Operations.patch)}1B.json`,
            InputFileA: null,
            InputFileB: null,
            ResultFilename: `${ConfigFormData.getPartOfName(ResultFilename, Operations.patch)}-1.json`,
            JobId: '',
            isDirty: false,
            isValid: false,
            ErrorData: { inputFileA: Utilities.emptyFile, inputFileB: Utilities.emptyFile, resultFile: null, ancestorFile: null } as ErrorData,
            ResultFileState: ResultFileState.initial,
            JobDescriptor: `${ConfigFormData.getPartOfName(JobDescriptor, Operations.patch)}`
        },
    }

}